import { ImageElementProps } from '@vindr/ui/src/components/common/StructuredReport/components/elements/image/ImageElement';
import type { StructuredReportElement } from '@vindr/ui/src/components/common/StructuredReport/components/elements/types';
import { getExtensionFileName } from '@vindr/utils/converter';
import { AxiosError, AxiosResponse } from 'axios';
import { CSSProperties } from 'react';
import S3Uploader, { S3UploaderConfig } from '../S3Uploader';
import api from '../api';
import { MIME_TO_EXTENSION } from '../constants/common.constant';
import { SORT_ORDER } from '../constants/filter.constant';
import { DefaultErrorResponse, DefaultResponse } from '../interfaces/index';
import { Section } from './templateStructuredReport';

const ELEMENT_URL = '/ris/structure-report/v1/elements';

export enum ELEMENT_TYPE {
  INPUT_TEXT = 'INPUT_TEXT',
  DROPDOWN_LIST = 'DROPDOWN_LIST',
  DATE_TIME = 'DATE_TIME',
  // Development
  GENERATED_LIST = 'GENERATED_LIST',
  STRUCTURE = 'STRUCTURE',
  IMAGE = 'IMAGE',
}

export enum ELEMENT_STATUS {
  DISABLED,
  ENABLED,
}

export interface Metadata {
  attribute?: Record<keyof CSSProperties, CSSProperties>;
  [key: string]: any;
}

export interface ImageMetadata extends Metadata {
  image_ids: string[];
  default_image?: {
    id?: string;
    src?: string;
    width: number;
    height: number;
    ratio: number;
  };
  images: {
    id?: string;
    src?: string;
    width: number;
    height: number;
    ratio: number;
  }[];
  width: number; //default width
  height: number; //default height
}

export interface SelectMetaData extends Metadata {
  default_value: string;
  items: {
    label: string;
    linked_id?: string;
    value: string;
    element?: StructuredReportElement; //? Have when render in Report
  }[];
}

interface WithDefaultTime {
  default_time: string;
  default_date?: string;
}

interface WithDefaultDate {
  default_time?: string;
  default_date: string;
}

export type DateTimeMetadata = Metadata & (WithDefaultTime | WithDefaultDate);
export interface iSelectionElement extends Element {
  type: ELEMENT_TYPE.DROPDOWN_LIST;
  metadata: SelectMetaData;
}

export interface iImageElement extends Element {
  type: ELEMENT_TYPE.IMAGE;
  metadata: null | ImageMetadata;
}

export interface iDateTimeElement extends Element {
  type: ELEMENT_TYPE.DATE_TIME;
  metadata: DateTimeMetadata;
}

export interface ParamsGetElement {
  limit?: number;
  offset?: number;
  sort_field?: '' | 'code' | 'name' | 'created_at' | 'updated_at';
  sort_order?: SORT_ORDER;
  created_at_from?: string;
  created_at_to?: string;
  types?: ELEMENT_TYPE[];
  name?: string;
  tags?: string[];
  created_by?: string;
  updated_at_from?: string;
  updated_at_to?: string;
}

export interface Element {
  code?: string;
  content: string;
  created_at?: string;
  created_by?: string;
  created_by_name?: string;
  detail_label?: string | null;
  detail_value?: string | null;
  default_value?: string | null;
  generated_number?: number | null;
  id: string;
  is_editable_detail_value?: boolean;
  is_editable_value?: boolean;
  is_free_text?: boolean;
  // is_multiple_selection_value?: boolean;
  is_show_detail_label?: boolean;
  is_show_label?: boolean;
  label?: string;
  elements?: Element[] | null;
  linked_element_ids?: string[] | null;
  metadata?: null | Metadata;
  name: string;
  status: ELEMENT_STATUS;
  tags?: null | string[];
  tooltip?: string;
  type: ELEMENT_TYPE;
  updated_at?: string;
  updated_by?: string;
}

export interface ResponseGetElement extends DefaultResponse {
  data: (Element | iSelectionElement | iDateTimeElement | iImageElement)[];
}
export const getElement = async (
  params: ParamsGetElement = {
    limit: 100,
    offset: 0,
  }
) => {
  const response = (await api({
    url: ELEMENT_URL + '/search',
    method: 'POST',
    data: params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetElement>;

  return response.data;
};

export interface ParamsGetElementDetail {
  id?: string;
  name?: string;
}

export interface ResponseGetElementDetail extends DefaultResponse {
  data: Element;
}

export const getElementDetail = async (params: ParamsGetElementDetail) => {
  const response = (await api({
    url: ELEMENT_URL + '/detail',
    method: 'GET',
    params,
    timeout: 30_000,
  })) as AxiosResponse<ResponseGetElementDetail>;

  return response.data;
};

export interface ParamsCreateElement {
  code: string;
  content: string;
  default_value?: string;
  detail_label?: string;
  detail_value?: string;
  generated_number?: number;
  is_editable_detail_value?: boolean;
  is_editable_value?: boolean;
  is_free_text?: boolean;
  // is_multiple_selection_value?: boolean;
  is_show_detail_label?: boolean;
  is_show_label?: boolean;
  label: string;
  linked_element_ids?: string[];
  metadata?: Metadata;
  name: string;
  tags?: string[];
  tooltip?: string;
  type: ELEMENT_TYPE;
}

export interface ResponseErrorCreateElement
  extends DefaultErrorResponse<'EMSG_31001' | 'EMSG_0001'> {}

export interface ResponseCreateElement extends DefaultResponse {
  data: Element;
}

export const createElement = async (data: ParamsCreateElement) => {
  try {
    const response = (await api({
      url: ELEMENT_URL,
      method: 'POST',
      data,
    })) as AxiosResponse<ResponseCreateElement>;

    return response?.data;
  } catch (error) {
    throw error as AxiosError<ResponseErrorCreateElement>;
  }
};

export const updateElement = async (id: string, data: ParamsCreateElement) => {
  const response = (await api({
    url: `${ELEMENT_URL}/${id}`,
    method: 'PUT',
    data,
  })) as AxiosResponse<ResponseCreateElement>;

  return response.data;
};

export const deleteElement = async (id: string) => {
  return (await api({
    url: `${ELEMENT_URL}/${id}`,
    method: 'DELETE',
  })) as AxiosResponse<any>;
};

const FILE_STORAGE = '/ris/file-storage/v1';
export interface ParamsUploadImageElement {
  action: 0;
  file_names: string[];
}

export interface ResponseUploadImageElement extends DefaultResponse {
  data: {
    presigned_objects: {
      file_name: string;
      id: string;
      method: string;
      url: string;
    }[];
  };
}

export const getURLUploadImageElement = async (
  data: ParamsUploadImageElement
) => {
  try {
    const response = (await api({
      url: FILE_STORAGE + '/request-upload',
      method: 'POST',
      data,
    })) as AxiosResponse<ResponseUploadImageElement>;

    return response?.data;
  } catch (error) {
    throw error as AxiosError<any>;
  }
};

export const uploadImageElement = async (url, file) => {
  try {
    const uploaderConfig: S3UploaderConfig = { url };
    const mimeType = MIME_TO_EXTENSION[getExtensionFileName(file.name)];
    if (mimeType) {
      uploaderConfig.headers = {
        'Content-Type': mimeType,
      };
    }
    const uploader = new S3Uploader(uploaderConfig);
    const dataToSend = file;
    return uploader.send(dataToSend);
  } catch (error) {
    throw error as AxiosError<any>;
  }
};

export interface ParamsDownloadImageElement {
  ids: string[];
}

export interface ResponseDownloadImageElement extends DefaultResponse {
  data: {
    id: string;
    url: string;
  }[];
}

export const downloadImageElement = async (
  data: ParamsDownloadImageElement
) => {
  try {
    const response = (await api({
      url: FILE_STORAGE + '/request-download',
      method: 'POST',
      data,
    })) as AxiosResponse<ResponseDownloadImageElement>;

    return response?.data;
  } catch (error) {
    throw error as AxiosError<any>;
  }
};

export const getImageUrl = async (data: ParamsDownloadImageElement) => {
  try {
    const response = (await api({
      url: FILE_STORAGE + '/request-download',
      method: 'POST',
      data,
    })) as AxiosResponse<ResponseDownloadImageElement>;

    return response?.data;
  } catch (error) {
    throw error as AxiosError<any>;
  }
};

export const setImageSrcToStructure = async (
  sections: Section[],
  getImageFunc = getImageUrl
): Promise<Section[]> => {
  const processImageElement = async (
    element: Element | ImageElementProps
  ): Promise<Element | ImageElementProps> => {
    if (element.type !== ELEMENT_TYPE.IMAGE) return element;

    const imageElement = element as ImageElementProps;
    const imageUrlsData = await getImageFunc({ ids: imageElement.image_ids });
    const imageUrls = imageUrlsData?.data;

    if (!imageUrls) return element;

    const imagesMap = new Map<string, typeof imageElement.images[0]>(
      (imageElement.images || []).map((img) => [img.id, img])
    );

    const images: ImageMetadata['images'] = imageUrls.map((imageUrl) => {
      const savedImage = imagesMap.get(imageUrl.id);
      return {
        ...savedImage,
        id: imageUrl.id,
        src: imageUrl.url,
        width: savedImage?.width || imageElement.default_width,
        height: savedImage?.height || imageElement.default_height,
        ratio:
          savedImage?.ratio ||
          imageElement.default_width / imageElement.default_height,
        manualAdjustment: savedImage?.manualAdjustment ?? true,
      };
    });

    return {
      ...element,
      images,
      metadata: {
        ...element.metadata,
        images,
      },
    };
  };

  const processSection = async (section: Section): Promise<Section> => {
    if (section.type !== ELEMENT_TYPE.STRUCTURE) return section;

    const updatedElements = await Promise.all(
      section.elements.map(processImageElement)
    );

    return { ...section, elements: updatedElements };
  };

  if (!sections?.length) return sections;
  return Promise.all(sections.map(processSection));
};
